import React, { memo, useCallback, useEffect, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ActionStepContentType, ActionStepStatus, ActionStepType } from 'constants/aiChat';
import { getConditionalObject } from 'utils/data';

import { AiChatTestIds } from '../../AiChat.testIds';

import { ToolConfirmationDispositionBadgeMap } from './ActionStep.constants';
import {
  actionStepAccordionDetailsStyles,
  actionStepAccordionStyles,
  actionStepAccordionSummaryNonExpandableStyles,
  actionStepAccordionSummaryStyles,
  actionStepDescriptionStyles,
  actionStepErrorPreviewStyles,
  actionStepSummaryWrapperStyles,
  activeActionStepAccordionStyles,
  replanPulseStyles,
} from './ActionStep.styles';
import { getAutoExpandedForStatus, isActionStepStatusActive } from './ActionStep.utils';
import ActionStepContent from './components/ActionStepContent';
import DependsOnPills from './components/DependsOnPills';
import StatusLine from './components/StatusLine';

const ActionStep = ({
  title = null,
  description = null,
  status,
  actionStepType = null,
  toolName = null,
  confirmationDisposition = null,
  content = [],
  expanded = null,
  onExpandedChange = null,
  isNewSinceReplan = false,
  errorPreview = null,
  dependsOn = null,
  stepKey = null,
  onDependencyClick = null,
  isHighlightedActive = null,
}) => {
  const isActive = isActionStepStatusActive(status);
  const isControlled = !_.isNil(expanded) && _.isFunction(onExpandedChange);
  const [internalExpanded, setInternalExpanded] = useState(() => getAutoExpandedForStatus(status));
  const [isUserOverride, setIsUserOverride] = useState(false);

  useEffect(() => {
    if (isControlled || isUserOverride) {
      return;
    }

    setInternalExpanded(getAutoExpandedForStatus(status));
  }, [status, isUserOverride, isControlled]);

  const onChange = useCallback(
    (_event, nextExpanded) => {
      if (isControlled) {
        onExpandedChange(nextExpanded);

        return;
      }

      setIsUserOverride(true);
      setInternalExpanded(nextExpanded);
    },
    [isControlled, onExpandedChange],
  );

  const showErrorPreview = status === ActionStepStatus.ERROR && _.isString(errorPreview);
  const hasDescription = _.isString(description) && description.length > 0;
  const hasContent = Array.isArray(content) && content.length > 0;
  const isExpandable = hasContent || hasDescription || showErrorPreview;

  const isExpanded = isExpandable && (isControlled ? !!expanded : internalExpanded);
  const shouldHighlight =
    isActive || (_.isBoolean(isHighlightedActive) ? isHighlightedActive : false);
  const baseStyles = shouldHighlight ? activeActionStepAccordionStyles : actionStepAccordionStyles;
  const accordionSx = isNewSinceReplan
    ? theme => ({ ...baseStyles, ...replanPulseStyles(theme) })
    : baseStyles;
  const summarySx = isExpandable
    ? actionStepAccordionSummaryStyles
    : actionStepAccordionSummaryNonExpandableStyles;
  const dispositionBadge = _.isNil(confirmationDisposition)
    ? null
    : ToolConfirmationDispositionBadgeMap[confirmationDisposition];

  return (
    <Accordion
      disableGutters
      data-testid={AiChatTestIds.ACTION_STEP_ACCORDION}
      expanded={isExpanded}
      sx={accordionSx}
      onChange={isExpandable ? onChange : _.noop}
      {...getConditionalObject(!_.isNil(stepKey), { 'data-stepkey': stepKey })}
    >
      <AccordionSummary sx={summarySx}>
        <Stack
          data-testid={AiChatTestIds.ACTION_STEP_SUMMARY_WRAPPER}
          sx={actionStepSummaryWrapperStyles}
        >
          <StatusLine
            actionStepType={actionStepType}
            isExpandable={isExpandable}
            isExpanded={isExpanded}
            isHighlightedActive={shouldHighlight}
            isNewSinceReplan={isNewSinceReplan}
            status={status}
            title={title ?? ''}
            toolName={toolName}
          />
          {dispositionBadge && (
            <LabelCDS title={dispositionBadge.title} variant={dispositionBadge.variant} />
          )}
          {description && (
            <Typography
              data-testid={AiChatTestIds.ACTION_STEP_DESCRIPTION}
              sx={actionStepDescriptionStyles}
              variant="regular12"
            >
              {description}
            </Typography>
          )}
          {showErrorPreview && (
            <Typography sx={actionStepErrorPreviewStyles} variant="regular12">
              {errorPreview}
            </Typography>
          )}
          {Array.isArray(dependsOn) && dependsOn.length > 0 && (
            <DependsOnPills dependsOn={dependsOn} onSelect={onDependencyClick} />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={actionStepAccordionDetailsStyles}>
        {content.map(({ id, contentProps, contentType }) => (
          <ActionStepContent contentProps={contentProps} contentType={contentType} key={id} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

ActionStep.propTypes = {
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  content: PropTypes.arrayOf(
    PropTypes.shape({
      contentType: PropTypes.oneOf(Object.values(ActionStepContentType)).isRequired,
      contentProps: PropTypes.object.isRequired,
    }),
  ),
  dependsOn: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
  errorPreview: PropTypes.string,
  expanded: PropTypes.bool,
  isNewSinceReplan: PropTypes.bool,
  stepKey: PropTypes.string,
  title: PropTypes.string,
  toolName: PropTypes.string,
  confirmationDisposition: PropTypes.string,
  isHighlightedActive: PropTypes.bool,
  onDependencyClick: PropTypes.func,
  onExpandedChange: PropTypes.func,
};

export default memo(ActionStep);
